import React from "react";
import { motion } from "framer-motion";
import {
  CircleDotDashed,
  CircleCheck,
  CircleX,
  Activity,
  Package,
} from "lucide-react";
import tw from "tailwind-styled-components";
import moment from "moment";
import { useNavigate } from "react-router-dom";

const RenderRecentDealStatus = ({ data }) => {
  if (data === "pending") {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium bg-yellow-50 text-yellow-700 ring-1 ring-yellow-600/20">
        <CircleDotDashed className="w-3.5 h-3.5" />
        {"Pending"}
      </div>
    );
  } else if (data === "approved") {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium bg-green-50 text-green-700 ring-1 ring-green-600/20">
        <CircleCheck className="w-3.5 h-3.5" />
        {"Approved"}
      </div>
    );
  } else if (data === "active") {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium bg-purple-50 text-purple-700 ring-1 ring-purple-600/20">
        <Activity className="w-3.5 h-3.5" />
        {"Live"}
      </div>
    );
  } else {
    return (
      <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium bg-red-50 text-red-700 ring-1 ring-red-600/20">
        <CircleX className="w-3.5 h-3.5" />
        {"Rejected"}
      </div>
    );
  }
};

const EmptyState = () => {
  return (
    <motion.div
      className="flex flex-col justify-center items-center px-6 py-12"
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <div className="relative mb-6">
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6C6C]/10 to-[#FF6C6C]/5 rounded-full blur-xl"></div>
        <div className="relative w-16 h-16 bg-gradient-to-br from-[#FF6C6C]/20 to-[#FF6C6C]/10 rounded-full flex items-center justify-center border border-[#FF6C6C]/20">
          <Package className="w-8 h-8 text-[#FF6C6C]" strokeWidth={1.5} />
        </div>
      </div>

      <div className="space-y-3 text-center">
        <motion.h3
          className="text-lg font-semibold text-gray-900"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1, duration: 0.3 }}
        >
          No Recent Deals
        </motion.h3>
        <motion.p
          className="text-gray-500 text-sm max-w-[200px] leading-relaxed"
          initial={{ opacity: 0, y: 10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          Your recent deals will appear here once you start creating them
        </motion.p>
      </div>

      <motion.div
        className="mt-6"
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.3, duration: 0.3 }}
      >
        <div className="flex gap-2 items-center text-xs text-gray-400">
          <div className="w-2 h-2 bg-[#FF6C6C]/30 rounded-full animate-pulse"></div>
          <span>Waiting for deals...</span>
        </div>
      </motion.div>
    </motion.div>
  );
};



const RecentDeals = ({ recentDeals }) => {
  const navigate = useNavigate()
  const handleDealNavigation = ()=>{
    navigate("/deals")
  }
  return (
    <motion.div
      className="p-6 bg-white rounded-lg"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div className="flex justify-between items-center">
        <h2 className="mb-2 font-semibold text-md">Recent Deals</h2>
        <button onClick={handleDealNavigation} className="py-2 px-4 bg- text-[#FF6C6C] text-sm font-semibold text-center hover:scale-105 transition duration-300 ease-in-out">
          View All
        </button>
      </div>
      {!recentDeals || recentDeals.length === 0 ? (
        <EmptyState />
      ) : (
        recentDeals.map((item, index) => {
          const { id, title, created_at, image, status, details } = item;
          return (
            <div key={id}>
              <div className="flex justify-between items-center">
                <div className="flex justify-between items-center w-full">
                  <div className="flex gap-3 items-center">
                    <img
                      src={image || "/placeholder-image.png"}
                      alt="Deal-Image"
                      width="55"
                      className="rounded-lg h-[55px] object-cover"
                    />
                    <div className="flex flex-col gap-2">
                      <h4 className="text-sm text-gray-600 lg:text-[11px] text-medium">
                        {title && title.length > 30
                          ? `${title.substring(0, 30)} ...`
                          : title || "No title"}
                      </h4>
                      <p className="text-xs text-gray-500 text-medium">
                        {details?.description && details.description.length > 15
                          ? `${details.description.substring(0, 15)} ...`
                          : details?.description || "No description"}
                      </p>
                    </div>
                  </div>
                  <div className="flex flex-col items-end">
                    <p className="mb-3 text-[10px] font-normal text-gray-500">
                      {moment(created_at).format("ll")}
                    </p>
                    <RenderRecentDealStatus data={status.toLowerCase()} />
                  </div>
                </div>
              </div>
              {index !== recentDeals.length ? <Seperator /> : <></>}
            </div>
          );
        })
      )}
    </motion.div>
  );
};

const Seperator = tw.div`w-full h-[1px] my-2 bg-gray-200`;
export default RecentDeals;
